import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { stacks } from '../data/stacks';

export default function StackDetail() {
  const { slug } = useParams();
  const stack = stacks.find(s => s.slug === slug);

  if (!stack) {
    return (
      <div style={{ paddingTop: '120px', textAlign: 'center' }}>
        <p>Stack not found. <Link to="/stacks" style={{ color: 'var(--clay)' }}>View all stacks</Link></p>
      </div>
    );
  }

  const billingLabel = stack.billingPeriod === 'monthly'
    ? 'per month'
    : `every ${stack.billingPeriod.replace('every ', '')}`;

  return (
    <>
      <Helmet defer={false}>
        <title>{stack.name} | Stacks by Hormonely</title>
        <meta name="description" content={`${stack.name} — ${stack.for}. A UK-made food supplement, one-off or on subscription.`} />
      </Helmet>

      {/* COMPACT HERO — name + buy box both visible on one screen */}
      <div style={{ background: stack.color, minHeight: '100vh', paddingTop: '88px', paddingBottom: '48px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 70% 40%, rgba(196,120,90,0.2) 0%, transparent 55%)', pointerEvents: 'none' }}></div>
        <div className="container" style={{ position: 'relative', zIndex: 1, width: '100%' }}>
          {/* stack-hero-grid (not inline gridTemplateColumns) so the mobile
              media query can collapse the fixed 380px buy-box column — it
              held the Subscribe/One-off buttons cramped on every phone width. */}
          <div className="stack-hero-grid">

            {/* Left — compact identity */}
            <div>
              <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--clay-lt)', marginBottom: '12px' }}>Stacks by Hormonely</div>
              <div style={{ display: 'inline-block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: '4px', background: 'rgba(196,120,90,0.2)', color: 'var(--clay-lt)', marginBottom: '14px' }}>{stack.badge}</div>
              <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(32px,4vw,52px)', color: 'var(--white)', lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: '14px' }}>
                {stack.name}
              </h1>
              <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, maxWidth: '440px', marginBottom: '24px' }}>{stack.for}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {['Free UK delivery on every order', stack.capsules, 'Cancel subscription any time'].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--clay)', flexShrink: 0 }}></div>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — buy box */}
            <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: '14px', padding: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
              <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(44,34,24,0.08)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: '40px', color: 'var(--clay)', lineHeight: 1 }}>£{stack.subscriptionPrice}</div>
                <div style={{ fontSize: '13px', color: 'var(--stone)', marginTop: '4px' }}>{billingLabel} · subscribe and save</div>
              </div>

              <a
                href={stack.stripeSubscription}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  padding: '16px',
                  background: 'var(--clay)',
                  color: 'var(--white)',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                }}
              >
                Subscribe — £{stack.subscriptionPrice}/{stack.billingPeriod === 'monthly' ? 'month' : stack.billingPeriod.replace('every ', '')}
              </a>

              <a
                href={stack.stripeOneOff}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  padding: '14px',
                  background: 'transparent',
                  color: 'var(--bark)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  border: '1.5px solid rgba(44,34,24,0.15)',
                  boxSizing: 'border-box',
                  marginBottom: '16px',
                }}
              >
                One-off purchase — £{stack.price}
              </a>

              <p style={{ fontSize: '11px', color: 'var(--stone)', lineHeight: 1.5, textAlign: 'center' }}>
                Secure checkout powered by Stripe. UK-made under GMP standards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* DETAILS — below the fold */}
      <section className="section section--warm">
        <div className="container">
          <div style={{ maxWidth: '720px' }}>
            <div className="rule"></div>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: '26px', color: 'var(--bark)', marginBottom: '14px' }}>About this stack</h2>
            <p style={{ fontSize: '16px', lineHeight: 1.75, color: '#4a3f36', marginBottom: '28px' }}>{stack.description}</p>

            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bark)', marginBottom: '10px' }}>Ingredients</h3>
            <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--stone)', marginBottom: '28px' }}>{stack.ingredients}</p>

            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bark)', marginBottom: '10px' }}>Suggested use</h3>
            <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--stone)', marginBottom: '28px' }}>{stack.dosage}</p>

            <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bark)', marginBottom: '10px' }}>Suitable for</h3>
            <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--stone)', marginBottom: '28px' }}>{stack.suitable}</p>

            <div style={{ padding: '16px 20px', background: 'rgba(196,120,90,0.07)', border: '1px solid rgba(196,120,90,0.15)', borderRadius: '8px', fontSize: '13px', color: 'var(--stone)', lineHeight: 1.6 }}>
              Food supplements should not replace a varied diet. Speak to your GP or pharmacist before starting anything new, especially if you take regular medicines.
            </div>
          </div>
        </div>
      </section>

      {/* OTHER STACKS */}
      <section className="section section--white" style={{ paddingTop: '48px', paddingBottom: '64px' }}>
        <div className="container">
          <div className="eyebrow" style={{ marginBottom: '20px' }}>Other stacks</div>
          <div className="stacks-grid">
            {stacks.filter(s => s.slug !== slug).slice(0, 4).map(s => (
              <Link key={s.slug} to={`/stacks/${s.slug}`} className="stack-card">
                <div className="stack-card__badge">{s.badge}</div>
                <div className="stack-card__name">{s.name}</div>
                <div className="stack-card__for">{s.for}</div>
                <div className="stack-card__price">£{s.subscriptionPrice} <span>/{s.billingPeriod === 'monthly' ? 'month' : s.billingPeriod.replace('every ', '')}</span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
